import {getId} from 'apis/model/base'
import classNames from 'classnames'
import copyToClipboard from 'copy-to-clipboard'
import {ROOT_URL} from 'envs/_current/config'
import getLinkToDetail from 'helpers/getLinkToDetail'
import React, {useContext, useMemo, useState} from 'react'
import createStickySider from 'views/AppLocals/factory/createStickySider'
import {renderIf} from 'views/Shared'
import {FollowButton} from '../../../../components/Feed/FollowButton'
import useTranslate from '../../../../modules/local/useTranslate'
import UserContext from '../UserContext'
import UserChannels from './UserChannels'
import UserOrganizations from './UserOrganizations'
import {Avatar, message} from "antd";
import {UserShareWidget} from "../../factory/createShareWidget";
import {HeaderButton} from "../../../Organization/components/PageHeader";
import {PiChats, PiLinkSimpleBold} from "react-icons/pi";
import {LayoutContext} from "../../../../components/layouts/Default/LayoutContext";
import {Link} from "react-router-dom";
import TooltipApp from "components/TooltipApp";
import {ConversationWith} from "../../factory/createChatButton";
import {WrapperBlockSider} from "../../Organization/components/WrapperBlockSider";
import {BsPersonFillGear} from "react-icons/bs";
import {channel_getChannelsByUser_Api, organization_getOrganizationsByUser_Api} from "../../../../apis";
import {renderLoading} from "../../functions/renderers/renderLoading";
import {bindQueryParam} from "../../functions/routerHelper";
import {BiSolidChurch} from "react-icons/bi";
import getAvatar from "../../../../helpers/getAvatar";
import getTitle from "../../../../helpers/getTitle";
import {OrgType} from "../../Organization/components/OrgType";
import CustomLazyPaginationWrapper from "../../../../modules/asyncCache/components/CustomLazyPaginationWrapper";
import {renderShowMore} from "../../../MainPage/functions/renderLimitLoadMore";
import {renderChannelItem} from "../../../MainPage/functions/renderChannelItem";
import {RiSignalTowerFill} from "react-icons/ri";
import {LoginContext} from "../../../../components/LoginContext";

const EmptySiderBlock = ({icon, title, message}) => {
    const t = useTranslate()
    return (
        <div className="p-3 flex items-center gap-2 rounded-xl border border-dashed border-color-100 background-50">
            {icon}
            <div className="flex flex-col gap-0.5 min-w-0">
                <span className="text-xs font-bold uppercase tracking-wide text-color-300 truncate">
                    {t(title)}
                </span>
                <span className="text-xs text-color-400 italic truncate">{message}</span>
            </div>
        </div>
    )
}

const ListWrapper = ({style, title, className, children}) => {
    const t = useTranslate()
    return (
        <div
            style={style}
            className={classNames('flex flex-col gap-3', className)}>
            <div className="uppercase text-color-300 font-medium pb-1 border-b border-color-50">
                {t(title)}
            </div>
            <div className="flex flex-col gap-3">{children}</div>
        </div>
    )
}

const Channels = () => {
    const t = useTranslate()
    const {user} = useContext(UserContext)
    const apiInfo = channel_getChannelsByUser_Api
    const refreshKey = useMemo(
        () => [apiInfo.path.replace(':id', user?.id)].join('/'),
        [user?.id]
    )
    const [limit, setLimit] = useState(3)

    return (
        <UserChannels
            user_id={getId(user)}
            renderWrap={(child, __, count) =>
                count > 0 ? (
                    <WrapperBlockSider
                        borderTop={true} borderColor="var(--color-secondary-200)" titleColor="text-blue-600"
                        icon={<RiSignalTowerFill size={18} className="text-blue-500"/>}
                        title="channels">
                        <div className="flex flex-col gap-4">
                            <CustomLazyPaginationWrapper
                                sample={limit}
                                auto={true}
                                key={refreshKey}
                                apiInfo={apiInfo}
                                renderLoading={renderLoading}
                                renderItem={renderChannelItem}
                                query={bindQueryParam({id: user?.id})}
                                renderLoadMoreButton={renderShowMore([limit, setLimit])}
                            />
                        </div>
                    </WrapperBlockSider>
                ) : (
                    <EmptySiderBlock
                        icon={<RiSignalTowerFill size={18} className="text-blue-300"/>}
                        title="channels"
                        message={t('no channels followed yet')}
                    />
                )
            }
        />
    )
}

const Organizations = () => {
    const t = useTranslate()
    const {user} = useContext(UserContext)
    const apiInfo = organization_getOrganizationsByUser_Api
    const refreshKey = useMemo(
        () => [apiInfo.path.replace(':id', user?.id)].join('/'),
        [user?.id]
    )
    const [limit, setLimit] = useState(5)

    return (
        <UserOrganizations
            user_id={getId(user)}
            renderWrap={(child, __, count) =>
                count > 0 ? (
                    <WrapperBlockSider
                        borderTop={true} borderColor="var(--color-secondary-200)" titleColor="text-blue-600"
                        icon={<BsPersonFillGear size={18} className="text-blue-500"/>}
                        title="join the community">
                        <div className="flex flex-col gap-4">
                            <CustomLazyPaginationWrapper
                                sample={limit}
                                auto={true}
                                key={refreshKey}
                                apiInfo={apiInfo}
                                renderLoading={renderLoading}
                                renderItem={(item) => (
                                    <div
                                        className="p-3 hover:shadow-md rounded-lg hover:scale-2xs flex flex-col gap-2 custom-item-render relative">
                                        <div className="flex items-start gap-2">
                                            <Avatar
                                                size={40}
                                                icon={<BiSolidChurch size={18} className="text-primary-600"/>}
                                                src={getAvatar(item)}
                                                className="flex flex-center bg-primary-50 rounded-md flex-shrink-0"
                                            />
                                            <div className="flex flex-col flex-1 min-w-0">
                                                <Link
                                                    to={getLinkToDetail(item)}
                                                    className="font-bold text-sm text-color-000 no-underline leading-snug truncate">
                                                    {getTitle(item)}
                                                </Link>
                                                <OrgType item={item}
                                                         className="font-medium text-2xs text-blue-500 italic leading-tight"/>
                                            </div>
                                        </div>
                                        <div className="flex justify-end">
                                            <FollowButton
                                                size="medium"
                                                item={item}
                                            />
                                        </div>
                                    </div>
                                )}
                                query={bindQueryParam({id: user?.id})}
                                renderLoadMoreButton={renderShowMore([limit, setLimit])}
                            />
                        </div>
                    </WrapperBlockSider>
                ) : (
                    <EmptySiderBlock
                        icon={<BsPersonFillGear size={18} className="text-blue-300"/>}
                        title="join the community"
                        message={t('not joined any organization yet')}
                    />
                )
            }
        />
    )
}

const Content = () => {
    const t = useTranslate()
    const {user} = useContext(UserContext)
    const {isSm} = useContext(LayoutContext)
    const login = useContext(LoginContext);
    const shareUrl = `${ROOT_URL}${getLinkToDetail(user)}`

    return (
        <div className="flex flex-col gap-6">
            {
                isSm && (
                    <div className="flex items-center gap-2 py-1">
                        {
                            login?.id !== user?.id && (
                                <FollowButton item={user} size="medium"/>
                            )
                        }
                        {renderIf(
                            user?.follow_status === true,
                            <ConversationWith
                                params={{receiver_id: getId(user)}}
                                render={(__, {onClick}) => (
                                    <div
                                        onClick={onClick}
                                        className="flex flex-center bg-blue-100 rounded-md text-blue-500 hover:text-blue-700 cursor-pointer"
                                        style={{width: 28, height: 28}}>
                                        <PiChats size={16}/>
                                    </div>
                                )}
                            />
                        )}
                        <div className="flex-1"/>
                        <UserShareWidget
                            className="gap-1.5"
                            footer={
                                <HeaderButton
                                    onClick={() => {
                                        copyToClipboard(shareUrl)
                                        message.success(t('copied'))
                                    }}
                                    width={24}
                                    height={24}
                                    icon={
                                        <TooltipApp
                                            title={t('copy link')}
                                            placement="right"
                                            classNames={{root: 'text-xs italic'}}>
                                            <PiLinkSimpleBold size={12}/>
                                        </TooltipApp>
                                    }
                                />
                            }
                        />
                    </div>
                )}
            {
                !isSm && (
                    <Channels/>
                )}
            {
                !isSm && (
                    <Organizations/>
                )}
            {
                !isSm && (
                    <React.Fragment>
                        <div className="h-3"/>
                        <UserShareWidget
                            className="gap-2"
                            header={
                                <span className="text-sm text-color-400 italic whitespace-nowrap">
                                {`${t('share')} : `}</span>
                            }
                            footer={
                                <HeaderButton
                                    onClick={() => {
                                        copyToClipboard(shareUrl)
                                        message.success(t('copied'))
                                    }}
                                    width={24}
                                    height={24}
                                    icon={
                                        <TooltipApp title={t('copy link')} placement="right">
                                            <PiLinkSimpleBold size={12}/>
                                        </TooltipApp>
                                    }
                                />
                            }
                        />
                        <div className="h-3"/>
                    </React.Fragment>
                )}
        </div>
    )
}

const UserSider = ({Wrapper = 'div', stickySider = true}) =>
    createStickySider({
        Wrapper,
        stickySider,
    })(Content)

export default UserSider
